import React, { useState } from "react";
import {
  Calendar,
  User,
  CheckCircle2,
  XCircle,
  Ban,
  Paperclip,
} from "lucide-react";
import {
  Card,
  CardContent,
  Badge,
  Button,
  Input,
  Alert,
} from "../ui";

const LEAVE_STATUS_VARIANTS = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "danger",
  CANCELLED: "default",
};

/** leaveType may be populated { _id, name, code, isPaid } or a plain id string */
function formatLeaveType(leaveType) {
  if (!leaveType) return "—";
  if (typeof leaveType === "object") {
    const name = leaveType.name || "Leave";
    const code = leaveType.code ? ` (${leaveType.code})` : "";
    const unpaid = leaveType.isPaid === false ? " · Unpaid" : "";
    return `${name}${code}${unpaid}`;
  }
  return String(leaveType);
}

export default function LeaveCard({
  leave,
  canManage,
  onApprove,
  onReject,
  onCancel,
  isOwn,
}) {
  const [actionType, setActionType] = useState(null); // 'APPROVE' | 'REJECT' | null
  const [approvalNote, setApprovalNote] = useState("");
  const [noteError, setNoteError] = useState("");

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    // startDate/endDate are "YYYY-MM-DD" strings — avoid timezone shift
    const d = new Date(dateStr.includes("T") ? dateStr : `${dateStr}T00:00:00`);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleActionConfirm = () => {
    if (actionType === "REJECT" && !approvalNote.trim()) {
      setNoteError("Rejection note is required");
      return;
    }
    if (actionType === "APPROVE") {
      onApprove?.(leave._id, { approvalNote });
    } else if (actionType === "REJECT") {
      onReject?.(leave._id, { approvalNote });
    }
    setActionType(null);
    setApprovalNote("");
    setNoteError("");
  };

  const typeLabel = formatLeaveType(leave.leaveType);

  return (
    <Card className="border border-neutral-200/60 shadow-none bg-white dark:bg-neutral-900 transition-all hover:border-neutral-300">
      <CardContent className="p-4 sm:p-5 flex flex-col justify-between h-full space-y-4">
        <div className="space-y-3">
          {/* Header Row: Type & Status Badges */}
          <div className="flex items-center justify-between gap-2">
            <Badge variant="primary" className="text-[10px] sm:text-xs px-2.5 py-0.5 font-medium">
              {typeLabel}
            </Badge>
            <Badge
              variant={LEAVE_STATUS_VARIANTS[leave.status] || "default"}
              className="text-[10px] sm:text-xs px-2.5 py-0.5 font-semibold shrink-0"
            >
              {leave.status}
            </Badge>
          </div>

          {/* Staff Info */}
          {leave.staffId && typeof leave.staffId === "object" && (
            <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              <User className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 shrink-0" />
              <span>{leave.staffId.name}</span>
              {leave.staffId.staffCode && (
                <span className="text-xs text-neutral-400 dark:text-neutral-500 font-normal">
                  ({leave.staffId.staffCode})
                </span>
              )}
            </div>
          )}

          {/* Date Duration & Days Pill */}
          <div className="flex items-center gap-2 text-xs sm:text-sm text-neutral-600 dark:text-neutral-300 flex-wrap">
            <Calendar className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500 shrink-0" />
            <span>
              {formatDate(leave.startDate)} → {formatDate(leave.endDate)}
            </span>
            <span className="bg-neutral-100 dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 text-[11px] font-medium px-2 py-0.5 rounded-md ml-auto shrink-0">
              {leave.totalDays} {leave.totalDays === 1 ? "day" : "days"}
            </span>
          </div>

          {/* Reason */}
          {leave.reason && (
            <p className="text-xs text-neutral-600 dark:text-neutral-300 bg-neutral-50/80 p-3 rounded-lg border border-neutral-100 dark:border-neutral-800 leading-relaxed">
              <span className="font-semibold text-neutral-800 dark:text-neutral-100">Reason: </span>
              {leave.reason}
            </p>
          )}

          {/* Attachment */}
          {leave.attachmentUrl && (
            <div>
              <a
                href={leave.attachmentUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium transition-colors"
              >
                <Paperclip className="w-3.5 h-3.5" /> View Attachment
              </a>
            </div>
          )}

          {/* Approval Note */}
          {leave.approvalNote && (
            <div className="text-xs bg-primary-50/60 border border-primary-100 text-primary-900 dark:text-primary-300 p-2.5 rounded-lg">
              <span className="font-semibold">Note: </span>
              {leave.approvalNote}
              {leave.approvedBy?.name && ` — by ${leave.approvedBy.name}`}
            </div>
          )}
        </div>

        {/* Action Panel for Pending Leaves */}
        {leave.status === "PENDING" && (
          <div className="pt-3 border-t border-neutral-100 dark:border-neutral-800">
            {actionType ? (
              <div className="space-y-2.5 bg-neutral-50 dark:bg-neutral-950 p-3 rounded-lg border border-neutral-200/80">
                <Input
                  label={
                    actionType === "APPROVE"
                      ? "Approval Note (Optional)"
                      : "Rejection Reason (Required)"
                  }
                  type="text"
                  value={approvalNote}
                  onChange={(e) => {
                    setApprovalNote(e.target.value);
                    setNoteError("");
                  }}
                  placeholder={
                    actionType === "APPROVE"
                      ? "Approved..."
                      : "Reason for rejection..."
                  }
                  className="bg-white dark:bg-neutral-900 text-xs"
                />

                {noteError && (
                  <Alert variant="danger" className="py-1 px-2 text-[11px]">
                    {noteError}
                  </Alert>
                )}

                <div className="flex gap-2 justify-end pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={() => {
                      setActionType(null);
                      setApprovalNote("");
                      setNoteError("");
                    }}
                    className="border border-neutral-200 dark:border-neutral-800 text-xs px-2.5 py-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant={actionType === "APPROVE" ? "primary" : "danger"}
                    size="xs"
                    onClick={handleActionConfirm}
                    className="text-xs px-3 py-1 font-semibold"
                  >
                    Confirm {actionType === "APPROVE" ? "Approve" : "Reject"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-end gap-2 flex-wrap">
                {isOwn && onCancel && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="xs"
                    onClick={() => onCancel(leave._id)}
                    leftIcon={<Ban className="w-3.5 h-3.5" />}
                    className="border border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 text-xs py-1.5 px-3"
                  >
                    Cancel Request
                  </Button>
                )}

                {canManage && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => setActionType("REJECT")}
                      leftIcon={<XCircle className="w-3.5 h-3.5" />}
                      className="bg-danger-50 text-danger-700 border border-danger-200 hover:bg-danger-100 text-xs font-semibold py-1.5 px-3"
                    >
                      Reject
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      size="xs"
                      onClick={() => setActionType("APPROVE")}
                      leftIcon={<CheckCircle2 className="w-3.5 h-3.5" />}
                      className="text-xs font-semibold py-1.5 px-3"
                    >
                      Approve
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}